/**
 * FixtureBadge — small annotation rendered on widgets backed by fixture data.
 *
 * Auto-hides once the backend reports `RA_DATALAKE_MODE=live` (via the
 * /api/analytics/_mode probe). No manual removal needed at cutover.
 *
 * Wrap any widget that consumes RA data:
 *
 *   <WidgetCard title="Active Users">
 *     <FixtureBadge />
 *     <Chart ... />
 *   </WidgetCard>
 *
 * James / next agent: cleanup item — once production datalake is wired and
 * verified, this entire component can be removed (search-and-delete) and the
 * `useRaMode` hook becomes unused. See .ai/context/RA_DATALAKE_HANDOFF.md
 * Section 6.7 (Cleanup) for the explicit checklist.
 */

import { useRaMode } from '@/hooks/useRaMode';
import { cn } from '@/lib/utils';

interface FixtureBadgeProps {
  /** Position within the widget. Default 'inline' (sits with widget content). */
  position?: 'inline' | 'corner';
  className?: string;
}

export function FixtureBadge({ position = 'inline', className }: FixtureBadgeProps) {
  const { mode } = useRaMode();
  if (mode !== 'fixture') return null;

  if (position === 'corner') {
    return (
      <span
        className={cn(
          'dls-absolute dls-top-2 dls-right-2 dls-z-10 dls-rounded-full',
          'dls-bg-status-warning/15 dls-text-status-warning',
          'dls-border dls-border-status-warning/30',
          'dls-px-2 dls-py-0.5 dls-text-[10px] dls-font-semibold dls-tracking-wider dls-uppercase',
          className
        )}
        title="This widget renders from fixture data. Real data lands when the RA datalake is wired in."
      >
        Fixture data
      </span>
    );
  }

  return (
    <div
      className={cn(
        'dls-inline-flex dls-items-center dls-gap-1.5 dls-rounded-full',
        'dls-bg-status-warning/10 dls-text-status-warning',
        'dls-border dls-border-status-warning/30',
        'dls-px-2 dls-py-0.5 dls-text-[10px] dls-font-semibold dls-tracking-wider dls-uppercase',
        className
      )}
      title="This widget renders from fixture data. Real data lands when the RA datalake is wired in."
    >
      <span className="dls-h-1.5 dls-w-1.5 dls-rounded-full dls-bg-status-warning" aria-hidden />
      Fixture data
    </div>
  );
}
